<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { useComparisonConfigStore } from "@/config/config-store";

import {
  formatAttributeValue,
  getCategoryForObject,
  getCharacteristicDescription,
} from "../comparison-utils";

import type { ComparisonObject } from "../comparison-data-service";

const props = defineProps<{
  columnLayout: {
    gridTemplateColumns: string;
    minWidth: string;
    stickyWidth: number;
  };
  typeBoundaryIndex: number;
  categoryName: string;
  objects: ComparisonObject[];
  subjectObject: ComparisonObject | undefined;
  hideIdentical: boolean;
}>();

const configStore = useComparisonConfigStore();
const { colors, capitalizeValues, fontSize } = storeToRefs(configStore);

const allCharacteristicNames = computed(() => {
  const names = new Set<string>();
  for (const obj of props.objects) {
    const category = getCategoryForObject(obj, props.categoryName);
    if (category) {
      for (const attribute of category.Attributes) {
        if (attribute.name) {
          names.add(attribute.name);
        }
      }
    }
  }
  return [...names];
});

function getCharacteristicValue(
  obj: ComparisonObject,
  characteristicName: string,
): string {
  const category = getCategoryForObject(obj, props.categoryName);
  const child = category?.Attributes.find((c) => c.name === characteristicName);
  const datatype = child?.meta?.datatype;
  const formatted = formatAttributeValue(child?.value, datatype ?? undefined);
  if (
    datatype === "numeric" ||
    datatype === "percentage" ||
    !capitalizeValues.value ||
    formatted === "–"
  ) {
    return formatted;
  }
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function isRowHidden(characteristicName: string): boolean {
  const values = props.objects.map((obj) =>
    getCharacteristicValue(obj, characteristicName),
  );
  const allIdentical = values.every((v) => v === values[0]);
  const allNull = props.objects.every((obj) => {
    const category = getCategoryForObject(obj, props.categoryName);
    const child = category?.Attributes.find(
      (c) => c.name === characteristicName,
    );
    return !child?.value;
  });
  return allIdentical || allNull;
}

const visibleCharacteristicNames = computed(() => {
  if (!props.hideIdentical) return allCharacteristicNames.value;
  return allCharacteristicNames.value.filter((name) => !isRowHidden(name));
});

function objectCellStyle(obj: ComparisonObject, index: number) {
  return {
    backgroundColor:
      obj === props.subjectObject
        ? colors.value.subjectRowBackground
        : colors.value.referenceRowBackground,
    ...(index === 0 ? { left: `${props.columnLayout.stickyWidth}px` } : {}),
  };
}
</script>

<template>
  <div
    :style="{
      backgroundColor: colors.referenceRowBackground,
      minWidth: columnLayout.minWidth,
    }"
  >
    <div
      v-for="charName in visibleCharacteristicNames"
      :key="charName"
      class="grid border-b border-gray-100 bg-white"
      :style="{ ...columnLayout, fontSize: `${fontSize}px` }"
    >
      <div class="sticky left-0 z-10 bg-white px-4 py-2.5 text-gray-600">
        <span>{{ charName }}</span>
        <i
          v-if="getCharacteristicDescription(objects, categoryName, charName)"
          v-tooltip.top="getCharacteristicDescription(objects, categoryName, charName)"
          class="pi pi-info-circle ml-1.5 cursor-help text-xs text-gray-400"
        />
      </div>
      <div
        v-for="(obj, index) in objects"
        :key="obj.name.value"
        class="px-4 py-2.5 text-center"
        :class="[
          index === 0 ? 'sticky z-10' : '',
          index === typeBoundaryIndex ? 'border-l-2 border-gray-300' : '',
        ]"
        :style="objectCellStyle(obj, index)"
      >
        {{ getCharacteristicValue(obj, charName) }}
      </div>
    </div>
  </div>
</template>
